import api from './api';
import publicApi from './publicApi';
import { writeProgramProjetsCache } from '../config/programProjetsCache';

export const getPublicSlides = async () => {
  const response = await publicApi.get('/public/slides');
  return response.data;
};

let slidesPrefetch = null;

export const preloadPublicSlides = () => {
  if (!slidesPrefetch) {
    slidesPrefetch = getPublicSlides().catch(() => null);
  }
  return slidesPrefetch;
};

export const getPublicProgrammes = async () => {
  const response = await publicApi.get('/public/programmes');
  return response.data;
};

let programmesPrefetch = null;

export const preloadPublicProgrammes = () => {
  if (!programmesPrefetch) {
    programmesPrefetch = getPublicProgrammes().catch(() => null);
  }
  return programmesPrefetch;
};

export const getProgrammeFromPrefetch = async (slug) => {
  const list = await preloadPublicProgrammes();
  if (!Array.isArray(list)) return null;
  return list.find((p) => p.slug === slug) || null;
};

export const getPublicProgramme = async (slug) => {
  const response = await publicApi.get(`/public/programmes/${slug}`);
  return response.data;
};

export const getPublicSiteSettings = async () => {
  const response = await publicApi.get('/public/site-settings');
  return response.data;
};

export const getAdminSlides = async () => {
  const response = await api.get('/admin/slides');
  return response.data;
};

const cmsWriteTimeout = (data) => {
  const hasDataUrl = (value) => typeof value === 'string' && value.startsWith('data:');
  const hasMedia = hasDataUrl(data?.image)
    || hasDataUrl(data?.video)
    || (Array.isArray(data?.gallery) && data.gallery.some(hasDataUrl));
  return hasMedia ? 120000 : 20000;
};

export const createSlide = async (data) => {
  const response = await api.post('/admin/slides', data, { timeout: cmsWriteTimeout(data) });
  return response.data;
};

export const updateSlide = async (id, data) => {
  const response = await api.put(`/admin/slides/${id}`, data, { timeout: cmsWriteTimeout(data) });
  return response.data;
};

export const deleteSlide = async (id) => {
  await api.delete(`/admin/slides/${id}`);
};

export const getAdminProgrammes = async () => {
  const response = await api.get('/admin/programmes');
  return response.data;
};

export const createProgramme = async (data) => {
  const response = await api.post('/admin/programmes', data, { timeout: cmsWriteTimeout(data) });
  return response.data;
};

export const updateProgramme = async (id, data) => {
  const response = await api.put(`/admin/programmes/${id}`, data, { timeout: cmsWriteTimeout(data) });
  return response.data;
};

export const deleteProgramme = async (id) => {
  await api.delete(`/admin/programmes/${id}`);
};

export const getAdminSiteSettings = async () => {
  const response = await api.get('/admin/site-settings');
  return response.data;
};

export const updateSiteSettings = async (settings) => {
  const response = await api.put('/admin/site-settings', { settings });
  return response.data;
};

export const getPublicHomepageStats = async (context = 'home') => {
  const response = await publicApi.get('/public/homepage-stats', { params: { context } });
  return response.data;
};

const HOMEPAGE_STATS_CACHE_KEY = 'khayr_homepage_stats_home_v1';

export const readHomepageStatsCache = () => {
  try {
    const raw = localStorage.getItem(HOMEPAGE_STATS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
};

const writeHomepageStatsCache = (stats) => {
  try {
    localStorage.setItem(HOMEPAGE_STATS_CACHE_KEY, JSON.stringify(stats));
  } catch {
    // ignore quota errors
  }
};

let homepageStatsPrefetch = null;

export const preloadPublicHomepageStats = (context = 'home') => {
  if (context !== 'home') {
    return getPublicHomepageStats(context).catch(() => null);
  }
  if (!homepageStatsPrefetch) {
    homepageStatsPrefetch = getPublicHomepageStats('home')
      .then((data) => {
        if (Array.isArray(data) && data.length) {
          writeHomepageStatsCache(data);
        }
        return data;
      })
      .catch(() => null);
  }
  return homepageStatsPrefetch;
};

export const getPublicProjets = async (options = {}) => {
  const params = {};
  if (options.featured) {
    params.featured = 1;
  }
  if (options.program) {
    params.program = options.program;
  }
  const response = await publicApi.get('/public/projets', { params });
  return response.data;
};

const projetsPrefetchBySlug = {};

export const preloadPublicProgramProjets = (slug) => {
  if (!slug) {
    return Promise.resolve(null);
  }
  if (!projetsPrefetchBySlug[slug]) {
    projetsPrefetchBySlug[slug] = getPublicProjets({ program: slug })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          writeProgramProjetsCache(slug, data);
        }
        return data;
      })
      .catch((error) => {
        delete projetsPrefetchBySlug[slug];
        return Promise.reject(error);
      });
  }
  return projetsPrefetchBySlug[slug];
};

export const getPublicPage = async (slug) => {
  const response = await publicApi.get(`/public/pages/${slug}`);
  return response.data;
};

export const getAdminHomepageStats = async (context) => {
  const response = await api.get('/admin/homepage-stats', { params: context ? { context } : {} });
  return response.data;
};

export const createHomepageStat = async (data) => {
  const response = await api.post('/admin/homepage-stats', data);
  return response.data;
};

export const updateHomepageStat = async (id, data) => {
  const response = await api.put(`/admin/homepage-stats/${id}`, data);
  return response.data;
};

export const deleteHomepageStat = async (id) => {
  await api.delete(`/admin/homepage-stats/${id}`);
};

export const getAdminProjets = async () => {
  const response = await api.get('/admin/projets');
  return response.data;
};

export const createProjet = async (data) => {
  const response = await api.post('/admin/projets', data, { timeout: cmsWriteTimeout(data) });
  return response.data;
};

export const updateProjet = async (id, data) => {
  const response = await api.put(`/admin/projets/${id}`, data, { timeout: cmsWriteTimeout(data) });
  return response.data;
};

export const deleteProjet = async (id) => {
  await api.delete(`/admin/projets/${id}`);
};

export const getAdminCmsPages = async () => {
  const response = await api.get('/admin/cms-pages');
  return response.data;
};

export const updateCmsPage = async (slug, data) => {
  const response = await api.put(`/admin/cms-pages/${slug}`, data);
  return response.data;
};

export const getPublicNavItems = async () => {
  const response = await publicApi.get('/public/nav-items');
  return response.data;
};

let navItemsPrefetch = null;

/** Démarre le chargement du menu dès le boot de l'app (évite le flash au 1er affichage). */
export const preloadPublicNavItems = () => {
  if (!navItemsPrefetch) {
    navItemsPrefetch = getPublicNavItems().catch(() => null);
  }
  return navItemsPrefetch;
};

export const getAdminNavItems = async () => {
  const response = await api.get('/admin/nav-items');
  return response.data;
};

export const createNavItem = async (data) => {
  const response = await api.post('/admin/nav-items', data);
  return response.data;
};

export const updateNavItem = async (id, data) => {
  const response = await api.put(`/admin/nav-items/${id}`, data);
  return response.data;
};

export const deleteNavItem = async (id) => {
  await api.delete(`/admin/nav-items/${id}`);
};
